import { useState, useEffect } from "react";
import { Check, Home, Diamond, Wrench, Shield } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface Props {
  brandColor: string;
  brandColorLight: string;
  brandColorMid: string;
  companyName: string;
  onComplete: () => void;
}

const VALUE_POINTS = [
  { icon: Home, text: "Custom-designed for your home" },
  { icon: Diamond, text: "Premium materials selected" },
  { icon: Wrench, text: "Professional installation included" },
  { icon: Shield, text: "Backed by craftsmanship warranty" },
];

export function BestValueTransition({ brandColor, brandColorLight, brandColorMid, companyName, onComplete }: Props) {
  const [progress, setProgress] = useState(0);
  const [visibleItems, setVisibleItems] = useState(0);

  useEffect(() => {
    // Progress bar: 0→100 over ~4s
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) { clearInterval(interval); return 100; }
        return prev + 2;
      });
    }, 80);

    // Stagger value points
    const timers = VALUE_POINTS.map((_, i) =>
      setTimeout(() => setVisibleItems(i + 1), 600 + i * 700)
    );

    // Auto-advance after 4s
    const done = setTimeout(onComplete, 4000);

    return () => {
      clearInterval(interval);
      timers.forEach(clearTimeout);
      clearTimeout(done);
    };
  }, [onComplete]);

  return (
    <div className="py-8 text-center animate-in fade-in-0 duration-500">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5" style={{ background: brandColorMid }}>
        <Diamond className="h-6 w-6" style={{ color: brandColor }} />
      </div>

      <h3 className="text-lg font-bold tracking-tight mb-1">Preparing Your Best Value Pricing</h3>
      <p className="text-xs text-muted-foreground mb-8 max-w-xs mx-auto leading-relaxed">
        Based on your selections, we're finalizing your custom project investment.
      </p>

      {/* Value reinforcement points */}
      <div className="space-y-3 mb-8 max-w-[280px] mx-auto text-left">
        {VALUE_POINTS.map((point, i) => {
          const Icon = point.icon;
          const visible = i < visibleItems;
          return (
            <div
              key={i}
              className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
              style={{ background: visible ? brandColorLight : "transparent" }}
            >
              <div className="p-1.5 rounded-lg shrink-0" style={{ background: brandColorMid }}>
                <Icon className="h-3.5 w-3.5" style={{ color: brandColor }} />
              </div>
              <span className="text-xs font-medium">{point.text}</span>
              {visible && <Check className="h-3.5 w-3.5 ml-auto shrink-0" style={{ color: brandColor }} />}
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="max-w-[200px] mx-auto mb-4">
        <Progress value={progress} className="h-1.5" />
      </div>

      {/* Branding */}
      <p className="text-[10px] font-semibold text-muted-foreground tracking-wider uppercase">{companyName}</p>
      <p className="text-[9px] text-muted-foreground/60 mt-0.5">Home of the Best Value Guarantee</p>
    </div>
  );
}
